import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal, Pencil, Trash2, GripHorizontal, RefreshCw } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import useBoardStore from '../../store/boardStore';
import { dialog } from '../../store/dialogStore';

const MENU_WIDTH_NORMAL = 176;
const MENU_WIDTH_TYPE = 220;

const COLUMN_TYPES = [
  { type: 'text', label: 'Texto', icon: 'Aa' },
  { type: 'status', label: 'Status', icon: '●' },
  { type: 'person', label: 'Pessoa', icon: '👤' },
  { type: 'date', label: 'Data', icon: '📅' },
  { type: 'number', label: 'Número', icon: '#' },
  { type: 'currency', label: 'Moeda (R$)', icon: 'R$' },
  { type: 'select', label: 'Select', icon: '▾' },
  { type: 'checkbox', label: 'Checkbox', icon: '☑' },
  { type: 'link', label: 'Link', icon: '🔗' },
];

export default function ColumnHeader({ column }) {
  const { updateColumn, deleteColumn, changeColumnType, fetchBoardData, currentBoard, editMode } = useBoardStore();
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(column.name);
  const [typePicker, setTypePicker] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const menuBtnRef = useRef(null);
  const popRef = useRef(null);
  const btnRectRef = useRef(null);
  const menuRef = useRef(null); // mantido apenas para compatibilidade residual
  const inputRef = useRef(null);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `col:${column.id}`,
    data: { type: 'column', columnId: column.id },
    disabled: !editMode,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  useEffect(() => setName(column.name), [column.name]);

  // Posicionamento via portal
  useLayoutEffect(() => {
    if (!menuOpen || !menuBtnRef.current) return;
    const recompute = () => {
      if (!menuBtnRef.current) return;
      const live = menuBtnRef.current.getBoundingClientRect();
      if (live.width > 0) btnRectRef.current = live;
      const rect = btnRectRef.current;
      if (!rect) return;
      const menuW = typePicker ? MENU_WIDTH_TYPE : MENU_WIDTH_NORMAL;
      const popHeight = popRef.current?.offsetHeight || 0;
      const vh = window.innerHeight;
      const spaceBelow = vh - rect.bottom;
      const flipUp = popHeight > 0 && spaceBelow < (popHeight + 8) && rect.top > spaceBelow;
      const top = flipUp
        ? rect.top + window.scrollY - popHeight - 4
        : rect.bottom + window.scrollY + 4;
      const left = rect.right + window.scrollX - menuW;
      setPos({ top, left });
    };
    recompute();
    const raf = requestAnimationFrame(recompute);
    window.addEventListener('scroll', recompute, true);
    window.addEventListener('resize', recompute);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', recompute, true);
      window.removeEventListener('resize', recompute);
    };
  }, [menuOpen, typePicker]);

  // Click-outside handler
  useEffect(() => {
    if (!menuOpen) { btnRectRef.current = null; return; }
    const handler = (e) => {
      const inBtn = menuBtnRef.current && menuBtnRef.current.contains(e.target);
      const inPop = popRef.current && popRef.current.contains(e.target);
      if (!inBtn && !inPop) {
        setMenuOpen(false);
        setTypePicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const saveName = () => {
    setEditing(false);
    const trimmed = name.trim();
    if (trimmed && trimmed !== column.name) updateColumn(column.id, { name: trimmed });
    else setName(column.name);
  };

  const handleDelete = async () => {
    setMenuOpen(false);
    if (await dialog.confirm(`Excluir coluna "${column.name}"`, 'Todos os valores desta coluna serão perdidos.', { confirmLabel: 'Excluir', danger: true })) {
      deleteColumn(column.id);
    }
  };

  const handleChangeType = async (newType) => {
    if (newType === column.type) { setTypePicker(false); setMenuOpen(false); return; }
    setTypePicker(false);
    setMenuOpen(false);
    const typeMeta = COLUMN_TYPES.find((t) => t.type === newType);
    const confirmed = await dialog.confirm(
      `Alterar tipo para "${typeMeta?.label ?? newType}"?`,
      'Os dados existentes serão convertidos. Esta ação não pode ser desfeita.',
      { confirmLabel: 'Alterar', danger: true }
    );
    if (!confirmed) return;
    try {
      await changeColumnType(column.id, newType);
      // Recarrega os card_values migrados do banco imediatamente.
      // Sem isso, a célula fica vazia até o próximo poll (30s).
      if (currentBoard?.id) {
        fetchBoardData(currentBoard.id).catch(() => {});
      }
    } catch (err) {
      console.error('changeColumnType error:', err);
    }
  };


  return (
    <div ref={setNodeRef} style={style} className="relative group/col flex items-center justify-center h-full px-2 py-2">
      {editMode && (
        <button
          {...attributes}
          {...listeners}
          className="absolute left-0 px-0.5 text-text-secondary/40 hover:text-text-primary cursor-grab active:cursor-grabbing opacity-0 group-hover/col:opacity-100 transition-opacity"
          title="Arrastar coluna"
        >
          <GripHorizontal className="w-3 h-3" />
        </button>
      )}
      {editing ? (
        <input
          ref={inputRef}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={saveName}
          onKeyDown={(e) => {
            if (e.key === 'Enter') saveName();
            if (e.key === 'Escape') { setName(column.name); setEditing(false); }
          }}
          className="w-full bg-transparent border border-accent/50 rounded px-1 py-0.5 outline-none text-sm font-medium text-text-primary text-center"
        />
      ) : (
        <span
          onDoubleClick={() => editMode && setEditing(true)}
          className={`col-header-text font-semibold truncate ${editMode ? 'cursor-text' : ''}`}
          title={editMode ? `${column.name} (duplo clique para renomear)` : column.name}
        >
          {column.name}
        </span>
      )}

      {editMode && (
        <button
          ref={menuBtnRef}
          onClick={() => setMenuOpen((v) => !v)}
          className="absolute right-0 p-0.5 rounded opacity-0 group-hover/col:opacity-100 hover:bg-white/10 text-text-secondary hover:text-text-primary transition-all"
          title="Opções"
        >
          <MoreHorizontal className="w-3.5 h-3.5" />
        </button>
      )}

      {menuOpen && createPortal(
        <div
          ref={popRef}
          style={{
            position: 'absolute',
            top: pos.top,
            left: pos.left,
            width: typePicker ? MENU_WIDTH_TYPE : MENU_WIDTH_NORMAL,
            zIndex: 9999,
          }}
          className="bg-card border border-border rounded-lg shadow-xl py-1"
        >
          {!typePicker ? (
            <>
              <button
                onClick={() => { setMenuOpen(false); setEditing(true); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-text-primary hover:bg-white/5 transition-colors"
              >
                <Pencil className="w-3.5 h-3.5" />
                Renomear
              </button>
              <button
                onClick={() => setTypePicker(true)}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-text-primary hover:bg-white/5 transition-colors"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Alterar tipo
              </button>
              <div className="h-px bg-border/50 my-1" />
              <button
                onClick={handleDelete}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-danger hover:bg-danger/10 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Excluir coluna
              </button>
            </>
          ) : (
            <>
              <div className="px-3 py-2 text-xs font-semibold text-text-secondary border-b border-border/50">
                Alterar tipo
              </div>
              <div className="py-1">
                {COLUMN_TYPES.map((ct) => (
                  <button
                    key={ct.type}
                    onClick={() => handleChangeType(ct.type)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-xs transition-colors ${
                      ct.type === column.type
                        ? 'text-accent bg-accent/10'
                        : 'text-text-primary hover:bg-white/5'
                    }`}
                  >
                    <span className="w-5 text-center text-sm">{ct.icon}</span>
                    {ct.label}
                    {ct.type === column.type && (
                      <span className="ml-auto text-accent text-xs">atual</span>
                    )}
                  </button>
                ))}
              </div>
              <div className="h-px bg-border/50" />
              <button
                onClick={() => setTypePicker(false)}
                className="w-full px-3 py-2 text-xs text-text-secondary hover:text-text-primary hover:bg-white/5 transition-colors text-left"
              >
                Voltar
              </button>
            </>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
